import { Form, Formik } from "formik";
import { PMDialog } from "../../../utils/PMDialog";
import * as Yup from "yup";
import { PMField } from "../../../utils/PMField";
import { useTranslation } from "react-i18next";
import { copyReport, createReportChecklist } from "../../../../api/service/report";
import PMSwitch from "../../../utils/PMSwitch";
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { QuillEditor } from "../../../utils/quill-editor";
import { EnhancedChecklistSelector } from "./enhanced-checklist-selector";
import { EnhancedItemsSelector } from "./enhanced-items-selector";
import { CustomerSelector } from "../../../utils/moduleSelectors/customerSelector";
import { runCopyReportOperation } from "./runCopyReportOperation";

export const ReportCopyForm = (props) => {
  const { onClose, report } = props;

  const item = report || {};

  const { t } = useTranslation();

  const copyOptions = useMemo(
    () => [
      { value: "customer", label: "Kunde" },
      { value: "items", label: "Ordrelinjer" },
      { value: "variables", label: t("report.copy.variables_option") },
      { value: "copy_price_lines", label: t("report.copy.price_lines_option") },
      { value: "order_date", label: "Order Date" },
      { value: "delivery_address", label: "Delivery Address" },
      { value: "system", label: "System" },
      { value: "executing_employee", label: "Executing Employee" },
      { value: "description", label: "Description" },
      { value: "internal_description", label: "Internal Description" },
    ],
    [t]
  );

  const [allSet, setAllSet] = useState(false);
  const [checklistSelectorOpen, setChecklistSelectorOpen] = useState(false);
  const [selectedChecklistConfig, setSelectedChecklistConfig] = useState(null);
  const [itemsSelectorOpen, setItemsSelectorOpen] = useState(false);
  const [selectedItemsConfig, setSelectedItemsConfig] = useState(null);

  const handleSetAll = (formik) => {
    setAllSet(!allSet);
    copyOptions.forEach((option) => {
      formik.setFieldValue(option.value, !allSet);
      // Initialize values when checkboxes are checked
      if (option.value === "customer" && !allSet && item?.customer?.id) {
        formik.setFieldValue("customer_id", item.customer.id);
      }
      if (option.value === "description" && !allSet && item?.description) {
        formik.setFieldValue("description_text", item.description);
      }
      if (option.value === "internal_description" && !allSet && item?.internal_description) {
        formik.setFieldValue("internal_description_text", item.internal_description);
      }
    });
  };

  const handleOptionChange = (optionValue, checked, formik) => {
    formik.setFieldValue(optionValue, checked);
    // Initialize values when checkboxes are checked
    if (optionValue === "customer" && checked && item?.customer?.id) {
      formik.setFieldValue("customer_id", item.customer.id);
    }
    if (optionValue === "description" && checked && item?.description) {
      formik.setFieldValue("description_text", item.description);
    }
    if (optionValue === "internal_description" && checked && item?.internal_description) {
      formik.setFieldValue("internal_description_text", item.internal_description);
    }
  };

  const navigate = useNavigate();
  const currentCompany = useSelector((state) => state.session.currentCompany);

  const handleChecklistSelect = (config) => {
    setSelectedChecklistConfig(config);
    setChecklistSelectorOpen(false);
  };

  const handleItemsSelect = (config) => {
    setSelectedItemsConfig(config);
    setItemsSelectorOpen(false);
  };

  const createChecklistForCopy = async (newReportId, config) => {
    if (!config || config.mode === "new") {
      await createReportChecklist({
        report: {
          id: newReportId,
        },
        fields: "id,checklist",
      });
    } else if (config.mode === "type") {
      await createReportChecklist({
        report: {
          id: newReportId,
          checklist_type_id: config.checklist_type_id,
          selected_item_ids: config.selected_item_ids,
        },
        fields: "id,checklist",
      });
    } else if (config.mode === "main_report" && config.items) {
      await createReportChecklist({
        report: {
          id: newReportId,
          copy_from_checklist_items: config.items,
        },
        fields: "id,checklist",
      });
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        id: item?.id || null,
        customer: false,
        customer_id: item?.customer?.id || null,
        variables: false,
        copy_price_lines: false,
        description: false,
        description_text: item?.description || "",
        internal_description: false,
        internal_description_text: item?.internal_description || "",
        submit: null,
      }}
      validationSchema={Yup.object({
        // customer_id: Yup.string().required("Customer is required"),
      })}
      onSubmit={async (values, helpers) => {
        try {
          helpers.setSubmitting(true);
          const submitValues = { ...values };
          if (values.description === true && values.description_text) {
            submitValues.description = values.description_text;
          }
          if (values.internal_description === true && values.internal_description_text) {
            submitValues.internal_description = values.internal_description_text;
          }
          if (values.customer === true) {
            submitValues.customer = values.customer_id || item?.customer?.id;
          }
          if (values.items === true && selectedItemsConfig) {
            submitValues.copy_items = selectedItemsConfig;
          }
          if (values.variables === true) {
            submitValues.copy_variables = true;
          }
          delete submitValues.variables;
          delete submitValues.description_text;
          delete submitValues.internal_description_text;

          const hasChecklist = Boolean(selectedChecklistConfig);
          if (typeof onClose === "function") {
            onClose(true);
          }
          await runCopyReportOperation({
            values: submitValues,
            report: item,
            companyId: currentCompany?.id,
            hasChecklistStep: hasChecklist,
            afterCopy: hasChecklist
              ? async (newReport) => {
                  await createChecklistForCopy(
                    newReport.id,
                    selectedChecklistConfig
                  );
                }
              : undefined,
            onCreated: (newReport) => {
              navigate(`/dashboard/service/reports/${newReport.id}`);
            },
            t,
            copyFn: copyReport,
          });
          helpers.setStatus({ success: true });
        } catch (err) {
          console.error(err);
          helpers.setStatus({ success: false });
          helpers.setErrors({
            submit: err?.message || t("operation_feedback.generic_error"),
          });
        } finally {
          helpers.setSubmitting(false);
        }
      }}
    >
      {(formik) => (
        <Form>
          <div className="grid grid-cols-6 gap-6">
            <div className="col-span-6 sm:col-span-6">
              <div className="mb-2">
                <PMSwitch
                  name="all"
                  label="All"
                  checked={copyOptions.every(
                    (option) => formik.values[option.value]
                  )}
                  onChange={() => handleSetAll(formik)}
                />
              </div>
              <span className="py-5 italic font-xs text-gray-400">
                Select all options
              </span>
            </div>

            {copyOptions &&
              copyOptions.map((option, idx) => (
                <div key={idx} className="col-span-6 sm:col-span-3">
                  <PMSwitch
                    name={option.value}
                    label={option.label}
                    onChange={(e) => handleOptionChange(option.value, e.target.checked, formik)}
                    {...formik}
                  />
                </div>
              ))}

            {/* Customer Selector - shown when customer is selected */}
            {formik.values.customer === true && (
              <div className="col-span-6 sm:col-span-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("Customer")} {item?.customer ? `(nåværende: ${item.customer.name || item.customer.displayTitle})` : ""}
                </label>
                <CustomerSelector
                  noLabel
                  label="Velg kunde"
                  selected={item?.customer}
                  {...formik}
                />
              </div>
            )}

            {/* Description Editor - shown when description is selected */}
            {formik.values.description === true && (
              <div className="col-span-6 sm:col-span-6">
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("Description")} (redigerbar)
                </label>
                <div className="mt-2">
                  <QuillEditor 
                    name="description_text" 
                    rows={4} 
                    {...formik}
                  />
                </div>
              </div>
            )}

            {/* Internal Description Editor - shown when internal_description is selected */}
            {formik.values.internal_description === true && (
              <div className="col-span-6 sm:col-span-6">
                <label
                  htmlFor="internal_description"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("Internal Description")} (redigerbar)
                </label>
                <div className="mt-2">
                  <QuillEditor 
                    name="internal_description_text" 
                    rows={4} 
                    {...formik}
                  />
                </div>
              </div>
            )}

            {/* Items Selection */}
            {formik.values.items === true && (
              <div className="col-span-6 sm:col-span-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Ordrelinjer
                    </label>
                    <button
                      type="button"
                      onClick={() => setItemsSelectorOpen(true)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {selectedItemsConfig
                        ? "Endre valg"
                        : "Velg ordrelinjer"}
                    </button>
                  </div>
                  {selectedItemsConfig ? (
                    <div className="mt-2 text-sm text-gray-600">
                      {selectedItemsConfig.mode === "new" && (
                        <span>Ingen ordrelinjer</span>
                      )}
                      {selectedItemsConfig.mode === "main_report" && (
                        <span>
                          Fra hovedrapport (
                          {selectedItemsConfig.include_all
                            ? "alle ordrelinjer"
                            : "valgte ordrelinjer"}{" "}
                          - {selectedItemsConfig.items?.length || 0} linjer)
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="mt-2 text-sm text-gray-500 italic">
                      Ingen ordrelinjer valgt
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Checklist Selection */}
            <div className="col-span-6 sm:col-span-6">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Sjekkliste
                  </label>
                  <button
                    type="button"
                    onClick={() => setChecklistSelectorOpen(true)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {selectedChecklistConfig
                      ? "Endre valg"
                      : "Velg sjekkliste"}
                  </button>
                </div>
                {selectedChecklistConfig ? (
                  <div className="mt-2 text-sm text-gray-600">
                    {selectedChecklistConfig.mode === "new" && (
                      <span>Ny tom sjekkliste</span>
                    )}
                    {selectedChecklistConfig.mode === "type" && (
                      <span>
                        Fra type: {selectedChecklistConfig.checklist_type_title || `Type ID: ${selectedChecklistConfig.checklist_type_id}`} (
                        {selectedChecklistConfig.selected_item_ids?.length || 0}{" "}
                        punkter valgt)
                      </span>
                    )}
                    {selectedChecklistConfig.mode === "main_report" && (
                      <span>
                        Fra hovedrapport (
                        {selectedChecklistConfig.include_all
                          ? "alle punkter"
                          : "kun ikke-godkjente"}{" "}
                        - {selectedChecklistConfig.items?.length || 0} punkter)
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="mt-2 text-sm text-gray-500 italic">
                    Ingen sjekkliste valgt
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
            <button
              type="button"
              className="inline-flex justify-center w-20 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
              onClick={onClose}
            >
              {t("Close")}{" "}
            </button>

            <button
              type="button"
              className="inline-flex align-right justify-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
              onClick={formik.submitForm}
            >
              {formik?.values?.id ? t("Update") : t("Create")}
            </button>
          </div>

          <EnhancedChecklistSelector
            open={checklistSelectorOpen}
            onClose={() => setChecklistSelectorOpen(false)}
            onSelect={handleChecklistSelect}
            mainReportChecklist={report?.checklist}
            mainReport={report}
          />

          <EnhancedItemsSelector
            open={itemsSelectorOpen}
            onClose={() => setItemsSelectorOpen(false)}
            onSelect={handleItemsSelect}
            mainReportItems={report?.items}
            mainReport={report}
          />
        </Form>
      )}
    </Formik>
  );
};

export const ReportCopyModal = (props) => {
  const { open, onClose, report } = props;

  return (
    <PMDialog open={open} onClose={onClose} title="Copy Report">
      <ReportCopyForm {...props} />
    </PMDialog>
  );
};
